/**
 * A-37: ヘッダーの管理画面リンククリック時に SSO ハンドオーバーで別オリジンへ遷移する
 * ナビゲータを生成する。
 *
 * 各管理アプリ（tenant-reservation / tenant-fuel / tenant-promo）は別サブドメインで動作し、
 * Firebase Auth セッションは origin scope で永続するためサブドメインを跨いで共有されない。
 * よって T-08 と同じく Custom Token を発行し、遷移先 URL の fragment（`#sso_token=`）で
 * 受け渡して target app 側で `signInWithCustomToken` させる。
 *
 * 本パッケージ（`@kawai/app-shell`）は Firebase 非依存を保つため、Custom Token を発行する
 * Callable は `issueSsoToken` として、現セッションの識別は `getSessionKey` として DI する
 * （各 app が自身の `callIssueSsoToken` と `auth.currentUser?.uid` を渡す）。
 *
 * S-91 第1弾: タブ hover（pointerenter）/ focus 時にトークンを先行発行してキャッシュし、
 * クリック時の Callable 往復（warm 0.2〜0.3s / cold 数秒）を遷移開始前の待ちから外す。
 * Custom Token は TTL 1 時間・一回性制約なしのため、短期キャッシュ（5 分）は安全余白が大きい。
 *
 * S-91 第2弾: 一度 SSO を確立した app（オリジン単位の localStorage 記録）へは
 * **トークンなしの素リンク**で遷移する。遷移先は自オリジンの永続セッションで起動でき、
 * (1) Callable 発行と signInWithCustomToken の両方が消える、(2) URL が fragment なしで
 * 静的になるため Speculation Rules prerender が完全一致で activate できる。
 * 遷移先のセッションが切れていた場合は遷移先 AuthGuard が portal へ bounce して
 * `?sso_retry=<feature>` で再発行を依頼する（`ssoRetryRedirect.ts` / 楽観記録の自己修復）。
 *
 * - トークンキャッシュは発行時のセッションキー（uid）に紐付ける。サインアウトや別ユーザーの
 *   再サインインを跨いだ再利用（前ユーザーのトークンで遷移する事故）を防ぐ
 * - 現在地（`isCurrent`）のリンクは遷移しない（no-op）
 * - 先行発行の失敗は握りつぶしてキャッシュから除去（クリック時に再発行される）
 * - クリック時の Callable 失敗は素のリンク URL に fall-through（target app のサインイン画面で受ける）
 * - A-34 と同じく同一ウィンドウ遷移（新タブにしない）
 */
/** S-91: 先行発行トークンの再利用期限。Custom Token TTL（1h）に対し十分小さく取る。 */
export const SSO_TOKEN_PREFETCH_TTL_MS = 5 * 60 * 1000;
/** S-91 第2弾: SSO 確立記録の localStorage キー接頭辞（値は JSON `{ sessionKey, host }`）。 */
const SSO_ESTABLISHED_KEY_PREFIX = 'kawai:sso-established:';
/** SSO 相互遷移の対象 feature（`clearAllEstablished` の掃除対象）。richmenu は未実装で除外。 */
const MANAGEMENT_FEATURES = [
    'reservation',
    'fuel',
    'promo',
];
function getDefaultStorage() {
    // プライベートブラウジング等で localStorage アクセス自体が throw する環境に備える。
    try {
        return window.localStorage;
    }
    catch {
        return null;
    }
}
/**
 * SSO ハンドオーバーのナビゲータ + 先行発行ハンドラを生成する。
 *
 * @param issueSsoToken targetApp に対する Custom Token を発行する関数（app が注入）
 * @param deps.getSessionKey 現セッションの識別子。`() => auth.currentUser?.uid ?? null` を
 *   渡す。null（サインアウト中）は先行発行をスキップし、キャッシュにも保存しない
 * @param deps.storage SSO 確立記録の保存先（テスト用 DI。既定は localStorage / 使用不能なら記録なし）
 * @param deps.now / deps.navigate テスト用 DI（既定は Date.now / location.href 代入）
 */
export function makeSsoAppNavigation(issueSsoToken, deps) {
    const { getSessionKey } = deps;
    const now = deps.now ?? Date.now;
    const navigate = deps.navigate ??
        ((url) => {
            window.location.href = url;
        });
    const storage = deps.storage === undefined ? getDefaultStorage() : deps.storage;
    const cache = new Map();
    // 確立記録は「どのセッション（uid + claims fingerprint）が / どのホストで」確立したかを
    // 保存する。現セッションの sessionKey・遷移先 host の両方が一致した場合のみ素リンク遷移を
    // 許可する。これにより (a) サインアウト（sessionKey=null）(b) ユーザー切替（uid 変化）
    // (c) impersonation 開始/終了（readOnly 等 claims 変化）(d) TP-33 のドメイン切替（host 変化）
    // のいずれでも記録がヒットせず、必ずトークン発行経路（現ユーザーで sign-in し直す）に戻る。
    const hasEstablished = (link) => {
        const sessionKey = getSessionKey();
        if (sessionKey === null)
            return false;
        try {
            const raw = storage?.getItem(SSO_ESTABLISHED_KEY_PREFIX + link.feature);
            if (raw == null)
                return false;
            const rec = JSON.parse(raw);
            return rec.sessionKey === sessionKey && rec.host === new URL(link.url).host;
        }
        catch {
            return false;
        }
    };
    const markEstablished = (link) => {
        const sessionKey = getSessionKey();
        if (sessionKey === null)
            return;
        try {
            storage?.setItem(SSO_ESTABLISHED_KEY_PREFIX + link.feature, JSON.stringify({ sessionKey, host: new URL(link.url).host }));
        }
        catch {
            // 記録できなくても遷移自体は成立する（次回もトークン付き遷移になるだけ）
        }
    };
    const clearEstablished = (feature) => {
        try {
            storage?.removeItem(SSO_ESTABLISHED_KEY_PREFIX + feature);
        }
        catch {
            // no-op
        }
    };
    const getOrIssue = (feature, prefetch) => {
        const sessionKey = getSessionKey();
        const hit = cache.get(feature);
        if (hit &&
            sessionKey !== null &&
            hit.sessionKey === sessionKey &&
            now() - hit.issuedAt < SSO_TOKEN_PREFETCH_TTL_MS) {
            return hit.promise;
        }
        const promise = issueSsoToken(feature, { prefetch });
        if (sessionKey !== null) {
            const entry = { promise, issuedAt: now(), sessionKey };
            cache.set(feature, entry);
            // 失敗した発行はキャッシュに残さない（次の hover / クリックで再発行させる）。
            promise.catch(() => {
                if (cache.get(feature) === entry)
                    cache.delete(feature);
            });
        }
        return promise;
    };
    const navigateWithToken = (link) => {
        void (async () => {
            try {
                const { customToken } = await getOrIssue(link.feature, false);
                // 楽観記録: target の sign-in 成否はここでは分からないが、失敗時は遷移先
                // AuthGuard の bounce（?sso_retry）が自己修復するため現セッション + host を残す。
                markEstablished(link);
                // Firebase Custom Token は base64url JWT のため encodeURIComponent は事実上 no-op。
                // 将来の token フォーマット変更時に fragment が壊れないための defensive。
                navigate(`${link.url}#sso_token=${encodeURIComponent(customToken)}`);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error('[sso] app link handover failed; falling back to plain link:', err);
                navigate(link.url);
            }
        })();
    };
    return {
        onPrefetchApp(link) {
            if (link.isCurrent)
                return;
            // 素リンク遷移になるタブはトークン不要（温めは Speculation Rules prerender が担う）。
            if (hasEstablished(link))
                return;
            // サインアウト中は発行しない（Callable は unauthenticated で必ず失敗するため）。
            if (getSessionKey() === null)
                return;
            // 失敗はここでは握りつぶす（getOrIssue がキャッシュ除去済み。クリック時に再試行）。
            void getOrIssue(link.feature, true).catch(() => undefined);
        },
        onNavigateApp(link) {
            if (link.isCurrent)
                return;
            if (hasEstablished(link)) {
                // S-91 第2弾: 確立済みはトークンなしの素リンク遷移（Callable も sign-in も不要。
                // prerender 済みならこの navigation が即 activate される）。
                navigate(link.url);
                return;
            }
            navigateWithToken(link);
        },
        onSsoRetryApp(link) {
            if (link.isCurrent)
                return;
            // bounce されてきた = 楽観記録が実態と乖離していたので破棄してトークン付きでやり直す。
            clearEstablished(link.feature);
            navigateWithToken(link);
        },
        clearAllEstablished() {
            // サインアウト時に呼ぶ。既知の管理 feature 記録を物理削除（残っても sessionKey 照合で
            // 素リンクにはならないが、記録の肥大と他ユーザーへの手掛かり残留を避ける）。
            for (const feature of MANAGEMENT_FEATURES)
                clearEstablished(feature);
        },
        selectPrerenderUrls(links) {
            return links.filter((l) => !l.isCurrent && hasEstablished(l)).map((l) => l.url);
        },
    };
}
//# sourceMappingURL=ssoNavigate.js.map